import type { Locale } from "@commerce-frontend/i18n/types";
import type { StoreOpeningHours, Store as StorePage } from "@commerce-frontend/types";
import type { DayOfWeek, OpeningHoursSpecification, Store, WithContext } from "schema-dts";
import { getOpeningTimes } from "../../app/[locale]/(regular-layout)/stores/_components/Info/OpeningHours";
import { type Day, days } from "../../app/[locale]/(regular-layout)/stores/_lib/date-time";
import { useStoreConfig } from "../store-config/context";

type Props = {
	store: StorePage;
	locale: Locale;
};

export const StorePageJsonLd = ({ store, locale }: Props) => {
	const config = useStoreConfig();

	const jsonLd: WithContext<Store> = {
		"@context": "https://schema.org",
		"@type": "Store",
		name: `Zeeman ${store.name}`,
		"@id": `${config.metadata.productionUrl}${locale.toLocaleLowerCase()}/store/${store.slug}`,
		url: `${config.metadata.productionUrl}${locale.toLocaleLowerCase()}/store/${store.slug}`,
		address: {
			"@type": "PostalAddress",
			streetAddress: `${store.address?.addressLine} ${store?.address?.streetNumber}`,
			addressLocality: store.address?.city ?? "",
			postalCode: store.address?.zipcode ?? "",
			addressCountry: store.address?.countryId ?? "",
		},
		openingHoursSpecification: getOpeningHoursSpecification(locale, store?.openingHours),
		telephone: store?.address?.phone ?? "",
		geo: {
			"@type": "GeoCoordinates",
			latitude: store?.coordinates?.lat,
			longitude: store?.coordinates?.lng,
		},
	};

	return (
		<script
			type="application/ld+json"
			dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
		/>
	);
};

/**
 * Generates the OpeningHoursSpecification for a specific day
 */
const getOpeningHoursSpecificationByDay = (
	locale: Locale,
	day: Day,
	openingHours: StoreOpeningHours,
): OpeningHoursSpecification[] => {
	const { openingTimes, state } = getOpeningTimes(locale, day, openingHours);

	// The store is closed on this day, so we don't include it in the schema
	if (!openingTimes) {
		return [];
	}

	const openingHoursSpecification: OpeningHoursSpecification[] = [];
	// Capitalize the first letter of the day to make it compatible with Schema.org
	const dayOfWeek = (day.charAt(0).toUpperCase() + day.slice(1)) as DayOfWeek;

	// Some stores have simple opening hours like 09:00 - 17:00
	// and some stores have a pause in the middle, 09:00 - 12:00, 13:00 - 17:00
	if (state === "no-pause") {
		openingHoursSpecification?.push({
			"@type": "OpeningHoursSpecification",
			dayOfWeek: dayOfWeek,
			opens: openingTimes[0],
			closes: openingTimes[1],
		});
	} else if (state === "pause") {
		openingHoursSpecification.push(
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: dayOfWeek,
				opens: openingTimes[0],
				closes: openingTimes[1],
			},
			{
				"@type": "OpeningHoursSpecification",
				dayOfWeek: dayOfWeek,
				opens: openingTimes[2],
				closes: openingTimes[3],
			},
		);
	}

	return openingHoursSpecification;
};

/**
 * Generates the OpeningHoursSpecification (includes all days)
 */
const getOpeningHoursSpecification = (
	locale: Locale,
	openingHours?: StoreOpeningHours | null,
): OpeningHoursSpecification[] => {
	if (!openingHours) {
		return [];
	}
	return days.flatMap((day) => getOpeningHoursSpecificationByDay(locale, day, openingHours));
};
